/**
 * Role Configuration Panel
 * New Architecture: API Pool + Role Assignments
 */

import React, { useState } from 'react';
import { Settings, Save, CheckCircle, XCircle, Layers, Users } from 'lucide-react';
import { useConfigStore } from '../store/useConfigStore';
import type { AIProvider } from '@shared/types';

// API Provider configurations
const apiProviders: Array<{
  key: 'deepseek' | 'doubao' | 'qwen' | 'openai' | 'anthropic';
  name: string;
  defaultModel: string;
  defaultTemperature: number;
}> = [
  { key: 'deepseek', name: 'DeepSeek', defaultModel: 'deepseek-chat', defaultTemperature: 0.7 },
  { key: 'doubao', name: '豆包 (Doubao)', defaultModel: 'ep-20250121151953-dhb2d', defaultTemperature: 0.7 },
  { key: 'qwen', name: '通义千问 (Qwen)', defaultModel: 'qwen-max', defaultTemperature: 0.7 },
  { key: 'openai', name: 'OpenAI', defaultModel: 'gpt-4o', defaultTemperature: 0.7 },
  { key: 'anthropic', name: 'Anthropic Claude', defaultModel: 'claude-3-5-sonnet-20241022', defaultTemperature: 0.7 },
];

// Role configurations
const roles: Array<{
  key: 'interpreter' | 'logicChecker' | 'solutionGenerator' | 'resultValidator';
  name: string;
  description: string;
  icon: string;
}> = [
  { key: 'interpreter', name: '解读者', description: '分析PDF文档，识别问题和结构', icon: '📖' },
  { key: 'logicChecker', name: '逻辑检查者', description: '验证推理步骤的逻辑连贯性', icon: '🔍' },
  { key: 'solutionGenerator', name: '解析制造者', description: '生成详细的解题步骤', icon: '✨' },
  { key: 'resultValidator', name: '结果验证者', description: '验证最终答案的准确性', icon: '✅' },
];

export const RoleConfigPanel: React.FC = () => {
  const {
    apiConfigs,
    roleAssignments,
    solutionGeneratorOptions,
    setAPIConfig,
    setRoleAssignment,
    setSolutionGeneratorOptions,
    isConfigured,
    isAPIConfigured,
  } = useConfigStore();

  const [mainTab, setMainTab] = useState<'api' | 'roles'>('api');
  const [activeAPI, setActiveAPI] = useState<'deepseek' | 'doubao' | 'qwen' | 'openai' | 'anthropic'>('deepseek');

  // Get or create API config
  const getCurrentAPIConfig = () => {
    return apiConfigs[activeAPI] || {
      provider: activeAPI as AIProvider,
      model: apiProviders.find(p => p.key === activeAPI)?.defaultModel || '',
      apiKey: '',
      temperature: apiProviders.find(p => p.key === activeAPI)?.defaultTemperature || 0.7,
      maxTokens: 4096,
    };
  };

  // Update API config
  const updateAPIConfig = (updates: Partial<typeof getCurrentAPIConfig>) => {
    const current = getCurrentAPIConfig();
    const updated = { ...current, ...updates };
    setAPIConfig(activeAPI, updated);
  };

  // Clear API config
  const clearAPIConfig = () => {
    setAPIConfig(activeAPI, null);
  };

  const currentAPIConfig = getCurrentAPIConfig();
  const isCurrentAPIConfigured = isAPIConfigured(activeAPI);

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
          <h2 className="text-2xl font-bold flex items-center space-x-2">
            <Settings className="w-6 h-6" />
            <span>AI 配置中心</span>
          </h2>
          <p className="mt-2 text-purple-100">
            配置 AI 提供商，然后为每个角色分配 API
          </p>
        </div>

        {/* Main Tabs */}
        <div className="grid grid-cols-2 border-b">
          <button
            onClick={() => setMainTab('api')}
            className={`
              p-4 text-base font-medium transition-all border-b-2 flex items-center justify-center space-x-2
              ${mainTab === 'api'
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-transparent hover:bg-gray-50 text-gray-600'
              }
            `}
          >
            <Layers className="w-5 h-5" />
            <span>API 配置池</span>
          </button>
          <button
            onClick={() => setMainTab('roles')}
            className={`
              p-4 text-base font-medium transition-all border-b-2 flex items-center justify-center space-x-2
              ${mainTab === 'roles'
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-transparent hover:bg-gray-50 text-gray-600'
              }
            `}
          >
            <Users className="w-5 h-5" />
            <span>角色分配</span>
          </button>
        </div>

        {/* API Configuration Tab */}
        {mainTab === 'api' && (
          <>
            {/* API Provider Tabs */}
            <div className="grid grid-cols-5 border-b bg-gray-50">
              {apiProviders.map((provider) => (
                <button
                  key={provider.key}
                  onClick={() => setActiveAPI(provider.key)}
                  className={`
                    p-3 text-sm font-medium transition-all border-b-2 flex items-center justify-center space-x-1
                    ${activeAPI === provider.key
                      ? 'border-purple-600 bg-white text-purple-700'
                      : 'border-transparent hover:bg-gray-100 text-gray-600'
                    }
                  `}
                >
                  {isAPIConfigured(provider.key) ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-300" />
                  )}
                  <span className="hidden md:inline">{provider.name}</span>
                  <span className="md:hidden">{provider.key}</span>
                </button>
              ))}
            </div>

            {/* API Configuration Form */}
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  {apiProviders.find(p => p.key === activeAPI)?.name} 配置
                </h3>
                {isCurrentAPIConfigured && (
                  <button
                    onClick={clearAPIConfig}
                    className="text-sm text-red-600 hover:text-red-700 underline"
                  >
                    清除配置
                  </button>
                )}
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  模型
                </label>
                <input
                  type="text"
                  value={currentAPIConfig.model}
                  onChange={(e) => updateAPIConfig({ model: e.target.value })}
                  placeholder={apiProviders.find(p => p.key === activeAPI)?.defaultModel}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="password"
                  value={currentAPIConfig.apiKey}
                  onChange={(e) => updateAPIConfig({ apiKey: e.target.value })}
                  placeholder="sk-..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature: {currentAPIConfig.temperature}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={currentAPIConfig.temperature}
                  onChange={(e) => updateAPIConfig({ temperature: parseFloat(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>精确 (0)</span>
                  <span>创造性 (1)</span>
                </div>
              </div>

              {/* Max Tokens */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  最大令牌数
                </label>
                <input
                  type="number"
                  value={currentAPIConfig.maxTokens || 4096}
                  onChange={(e) => updateAPIConfig({ maxTokens: parseInt(e.target.value) })}
                  min="1"
                  max="32000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Status */}
              <div className={`p-4 rounded-lg ${isCurrentAPIConfigured ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center space-x-2">
                  {isCurrentAPIConfigured ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">已配置</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">未配置 - 请填写 API Key</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Role Assignment Tab */}
        {mainTab === 'roles' && (
          <div className="p-6 space-y-6">
            <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p>💡 为每个 AI 角色选择使用哪个已配置的 API 提供商</p>
            </div>

            {/* Roles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roles.map((role) => {
                const assigned = roleAssignments[role.key];
                const isAssignedConfigured = assigned ? isAPIConfigured(assigned) : false;

                return (
                  <div
                    key={role.key}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-all"
                  >
                    <div className="flex items-start space-x-3 mb-3">
                      <span className="text-3xl">{role.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{role.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        使用的 API
                      </label>
                      <select
                        value={assigned || ''}
                        onChange={(e) => setRoleAssignment(role.key, e.target.value as any || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      >
                        <option value="">-- 请选择 --</option>
                        {apiProviders.map((provider) => (
                          <option
                            key={provider.key}
                            value={provider.key}
                            disabled={!isAPIConfigured(provider.key)}
                          >
                            {provider.name} {!isAPIConfigured(provider.key) ? '(未配置)' : '✓'}
                          </option>
                        ))}
                      </select>
                    </div>

                    {assigned && (
                      <div className={`mt-3 flex items-center space-x-2 text-xs ${isAssignedConfigured ? 'text-green-600' : 'text-red-600'}`}>
                        {isAssignedConfigured ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>已配置并可用</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4" />
                            <span>API 未配置</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Solution Generator Options */}
            {roleAssignments.solutionGenerator && (
              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-800 mb-4">✨ 解析制造者 - 额外选项</h4>
                <div className="space-y-4">
                  {/* Detail Level */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      详细程度
                    </label>
                    <select
                      value={solutionGeneratorOptions.detailLevel}
                      onChange={(e) => setSolutionGeneratorOptions({ detailLevel: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="basic">基础</option>
                      <option value="detailed">详细</option>
                      <option value="comprehensive">全面</option>
                    </select>
                  </div>

                  {/* Checkboxes */}
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={solutionGeneratorOptions.stepByStep}
                        onChange={(e) => setSolutionGeneratorOptions({ stepByStep: e.target.checked })}
                        className="rounded text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">分步解析</span>
                    </label>

                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={solutionGeneratorOptions.includeVerification}
                        onChange={(e) => setSolutionGeneratorOptions({ includeVerification: e.target.checked })}
                        className="rounded text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">包含验证</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Global Status Footer */}
        <div className="p-6 bg-gray-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isConfigured() ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm font-medium">
                {isConfigured() ? '✅ 所有角色已配置且可用' : '⚠️ 请完成所有配置'}
              </span>
            </div>
            <Save className="w-5 h-5 text-gray-400" />
          </div>

          {/* Configuration Summary */}
          {isConfigured() && (
            <div className="mt-3 text-xs text-gray-600 bg-white rounded p-3 border">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {roles.map((role) => {
                  const assigned = roleAssignments[role.key];
                  return (
                    <div key={role.key}>
                      <span className="font-medium">{role.icon} {role.name}:</span>{' '}
                      <span className="text-purple-600">
                        {assigned ? apiProviders.find(p => p.key === assigned)?.name : '未分配'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
